import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import {
    Paper,
    Button,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Skeleton,
} from '@mui/material';
import axios from '../config/axiosConfig';

// ─── Constants ────────────────────────────────────────────────────────────────

const DESKTOP_TIMEFRAMES = ['5D', '1M', '6M', 'YTD', '1Y', '5Y', 'MAX'];
const MOBILE_TIMEFRAMES = ['1M', '6M', '1Y', 'MAX'];

const chartTypes = [
    { value: 'candlestick', label: 'Candle' },
    { value: 'line', label: 'Line' },
    { value: 'bar', label: 'Bar' },
    { value: 'mountain', label: 'Mountain' },
    { value: 'hollowCandle', label: 'Hollow Candle' },
    { value: 'candleWithVolume', label: 'Candle + Vol' },
];

const upColor = '#00da3c';
const upBorderColor = '#008F28';
const downColor = '#ec0000';
const downBorderColor = '#8A0000';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getSign = (data, dataIndex, openVal, closeVal) => {
    if (openVal > closeVal) return -1;
    if (openVal < closeVal) return 1;
    return dataIndex > 0 ? (data[dataIndex - 1].close <= closeVal ? 1 : -1) : 1;
};

const filterDataByTimeframe = (data, timeframe) => {
    const now = new Date();
    switch (timeframe) {
        case '5D': return data.slice(-5);
        case '1M': return data.filter(e => new Date(e.date) >= new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()));
        case '6M': return data.filter(e => new Date(e.date) >= new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()));
        case 'YTD': return data.filter(e => new Date(e.date) >= new Date(now.getFullYear(), 0, 1));
        case '1Y': return data.filter(e => new Date(e.date) >= new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()));
        case '5Y': return data.filter(e => new Date(e.date) >= new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()));
        default: return data;
    }
};

const formatPrice = (val) => {
    if (val == null) return '—';
    return parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateLabel = (value) => {
    const date = new Date(value);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
};

// ─── OHLC Header ─────────────────────────────────────────────────────────────

const OHLCHeader = ({ ohlc, isMobile, theme }) => {
    if (!ohlc) return null;

    const { date, open, high, low, close, volume, change, changePct } = ohlc;
    const isPositive = change >= 0;
    const changeColor = isPositive ? upColor : downColor;

    if (isMobile) {
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
                minHeight: 36,
            }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                    {date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {formatPrice(close)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: changeColor, fontWeight: 600, fontSize: '0.75rem' }}>
                        {isPositive ? '+' : ''}{formatPrice(change)} ({isPositive ? '+' : ''}{changePct?.toFixed(2)}%)
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexWrap: 'wrap',
            minHeight: 40,
            backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.02)'
                : 'rgba(0,0,0,0.01)',
        }}>
            {date && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, minWidth: 90 }}>
                    {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
            )}
            {[
                { label: 'O', value: open },
                { label: 'H', value: high },
                { label: 'L', value: low },
                { label: 'C', value: close },
            ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                        {formatPrice(value)}
                    </Typography>
                </Box>
            ))}
            {volume != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>Vol</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                        {volume >= 1e9 ? `${(volume / 1e9).toFixed(2)}B` : volume >= 1e6 ? `${(volume / 1e6).toFixed(2)}M` : volume >= 1e3 ? `${(volume / 1e3).toFixed(2)}K` : volume}
                    </Typography>
                </Box>
            )}
            {change != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <Typography variant="caption" sx={{ color: changeColor, fontWeight: 700, fontSize: '0.78rem' }}>
                        {isPositive ? '+' : ''}{formatPrice(change)} ({isPositive ? '+' : ''}{changePct?.toFixed(2)}%)
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const InteractiveStockChart = ({ symbol }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isTablet = useMediaQuery('(max-width:960px)');
    const chartRef = useRef(null);

    const [chartOptions, setChartOptions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [selectedTimeframe, setSelectedTimeframe] = useState('YTD');
    const [selectedChartType, setSelectedChartType] = useState(isMobile ? 'line' : 'candlestick');
    const [ohlc, setOhlc] = useState(null);

    const timeframes = isMobile ? MOBILE_TIMEFRAMES : DESKTOP_TIMEFRAMES;

    const getSeriesOptions = useCallback((type) => {
        const baseCandleSeries = {
            name: 'Candle',
            type: 'candlestick',
            itemStyle: {
                color: upColor,
                color0: downColor,
                borderColor: upBorderColor,
                borderColor0: downBorderColor,
            },
            encode: { x: 0, y: [1, 2, 3, 4] },
        };

        const baseLineSeries = {
            name: 'Price',
            type: 'line',
            encode: { x: 0, y: 2 },
            smooth: true,
            showSymbol: false,
            lineStyle: { color: theme.palette.primary.main, width: 2 },
            areaStyle: type === 'mountain' ? {
                color: {
                    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                        { offset: 0, color: `${theme.palette.primary.main}40` },
                        { offset: 1, color: `${theme.palette.primary.main}00` },
                    ],
                }
            } : undefined,
        };

        switch (type) {
            case 'candlestick': return [baseCandleSeries];
            case 'line': return [{ ...baseLineSeries, areaStyle: undefined }];
            case 'mountain': return [baseLineSeries];
            case 'bar': return [{
                name: 'Bar', type: 'bar',
                encode: { x: 0, y: 2 },
                itemStyle: { color: theme.palette.primary.main },
            }];
            case 'hollowCandle': return [{
                ...baseCandleSeries,
                itemStyle: { ...baseCandleSeries.itemStyle, color: 'transparent', color0: downColor },
            }];
            case 'candleWithVolume': return [
                baseCandleSeries,
                {
                    name: 'Volume', type: 'bar',
                    xAxisIndex: 1, yAxisIndex: 1,
                    large: true,
                    encode: { x: 0, y: 5 },
                    visualMap: false,
                    itemStyle: { color: '#7f7f7f' },
                },
            ];
            default: return [baseCandleSeries];
        }
    }, [theme.palette.primary.main]);

    const buildChartOption = useCallback((data, timeframe, chartType) => {
        const fd = filterDataByTimeframe(data, timeframe);

        if (fd.length === 0) return null;

        // Set initial OHLC to last data point
        const last = fd[fd.length - 1];
        const prev = fd[fd.length - 2];
        const change = prev ? last.close - prev.close : 0;
        const changePct = prev ? (change / prev.close) * 100 : 0;
        setOhlc({ date: last.date, open: last.open, high: last.high, low: last.low, close: last.close, volume: last.volume, change, changePct });

        const chartData = fd.map((entry, index) => {
            const sign = getSign(fd, index, entry.open, entry.close);
            return [entry.date, entry.open, entry.close, entry.low, entry.high, entry.volume, sign];
        });

        const showVolumeChart = chartType === 'candleWithVolume';
        const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
        const textColor = theme.palette.text.secondary;

        // Current price line
        const lastClose = last.close;
        const markLine = {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: theme.palette.primary.main, width: 1, opacity: 0.7 },
            label: {
                show: true,
                position: 'end',
                formatter: () => formatPrice(lastClose),
                color: '#ffffff',
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                padding: [3, 7],
                borderRadius: 3,
            },
            data: [{ yAxis: lastClose }],
        };

        const grid = showVolumeChart
            ? [{ left: '2%', right: '6%', top: '3%', bottom: '30%', containLabel: true },
            { left: '2%', right: '6%', height: 60, bottom: '15%', containLabel: true }]
            : { left: isMobile ? '8px' : '12px', right: isMobile ? '70px' : '80px', bottom: '24px', top: '12px', containLabel: false };

        const xAxisBase = {
            type: 'category',
            boundaryGap: true,
            axisLine: { onZero: false, lineStyle: { color: gridColor } },
            splitLine: { show: false },
            axisTick: { show: false },
            min: 'dataMin',
            max: 'dataMax',
            axisLabel: {
                color: textColor,
                fontSize: isMobile ? 9 : 11,
                formatter: formatDateLabel,
                hideOverlap: true,
            },
        };

        const xAxis = showVolumeChart
            ? [xAxisBase, { ...xAxisBase, gridIndex: 1, axisLabel: { show: false }, axisTick: { show: false } }]
            : xAxisBase;

        const yAxisBase = {
            scale: true,
            splitArea: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            position: 'right',
            axisLabel: {
                color: textColor,
                fontSize: isMobile ? 9 : 11,
                formatter: (val) => formatPrice(val),
            },
            splitLine: { show: true, lineStyle: { color: gridColor, type: 'solid' } },
        };

        const yAxis = showVolumeChart
            ? [yAxisBase, { scale: true, gridIndex: 1, splitNumber: 2, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false } }]
            : yAxisBase;

        const dataZoom = [{
            type: 'inside',
            xAxisIndex: showVolumeChart ? [0, 1] : [0],
            start: 0, end: 100,
            zoomOnMouseWheel: !isMobile,
            moveOnMouseMove: !isMobile,
            moveOnMouseWheel: false,
            zoomLock: isMobile,
            preventDefaultMouseMove: !isMobile,
        }];

        const series = getSeriesOptions(chartType).map((s, i) => {
            // Add markLine only to the first non-volume series
            if (i === 0 && !showVolumeChart) return { ...s, markLine };
            if (i === 0 && showVolumeChart) return { ...s, markLine };
            return s;
        });

        return {
            animation: false,
            dataset: { source: chartData },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', crossStyle: { color: theme.palette.text.disabled, width: 1 }, label: { show: false } },
                backgroundColor: 'transparent',
                borderWidth: 0,
                padding: 0,
                formatter: (params) => {
                    if (!params || !params[0]) return '';
                    const d = params[0].data;
                    const [date, open, close, low, high, volume] = d;
                    const prevIdx = fd.findIndex(e => e.date === date);
                    const prevEntry = prevIdx > 0 ? fd[prevIdx - 1] : null;
                    const chg = prevEntry ? close - prevEntry.close : 0;
                    const chgPct = prevEntry ? (chg / prevEntry.close) * 100 : 0;
                    setOhlc({ date, open, high, low, close, volume, change: chg, changePct: chgPct });
                    return ''; // Header handles display
                },
            },
            grid,
            xAxis,
            yAxis,
            dataZoom,
            visualMap: showVolumeChart ? {
                show: false, seriesIndex: 1, dimension: 6,
                pieces: [{ value: 1, color: upColor }, { value: -1, color: downColor }],
            } : null,
            series,
        };
    }, [theme, isMobile, getSeriesOptions]);

    const updateChart = useCallback((data, timeframe, chartType) => {
        const option = buildChartOption(data, timeframe, chartType);
        if (option) setChartOptions(option);
    }, [buildChartOption]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setOhlc(null);
        axios.get(`/api/historicaldata/${symbol}`)
            .then((response) => {
                const data = response.data.data[0].historical
                    .sort((a, b) => new Date(a.date) - new Date(b.date));
                setHistoricalData(data);
                updateChart(data, selectedTimeframe, selectedChartType);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching chart data:', error);
                setError(error.message);
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbol]);

    useEffect(() => {
        if (historicalData.length) {
            updateChart(historicalData, selectedTimeframe, selectedChartType);
        }
    }, [selectedTimeframe, selectedChartType, historicalData, updateChart]);

    useEffect(() => {
        if (isMobile) {
            setSelectedChartType('line');
        }
    }, [isMobile]);

    const chartHeight = isMobile ? '260px' : isTablet ? '380px' : '480px';

    if (loading) {
        return (
            <Paper elevation={3} variant="stats" sx={{ p: 0, height: '100%', minHeight: chartHeight, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Skeleton width={300} height={20} />
                </Box>
                <Box sx={{ flex: 1, p: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
                </Box>
                <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1 }}>
                    {[...Array(5)].map((_, i) => <Skeleton key={i} width={40} height={30} sx={{ borderRadius: 1 }} />)}
                </Box>
            </Paper>
        );
    }

    if (error || !chartOptions) {
        return (
            <Paper elevation={3} variant="stats" sx={{ height: chartHeight, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>No chart data available</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Please try again later</Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={3}
            variant="stats"
            sx={{ p: 0, height: '100%', minHeight: chartHeight, display: 'flex', flexDirection: 'column' }}
        >
            {/* OHLC Header */}
            <OHLCHeader ohlc={ohlc} isMobile={isMobile} theme={theme} />

            {/* Chart */}
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <ReactECharts
                    ref={chartRef}
                    option={chartOptions}
                    style={{ height: '100%', width: '100%', minHeight: isMobile ? '220px' : '480px' }}
                    notMerge={true}
                />
            </Box>

            {/* Controls */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 0.5, sm: 1 },
                p: { xs: 1, sm: 1.5 },
                borderTop: `1px solid ${theme.palette.divider}`,
            }}>
                {/* Chart type — hide on mobile */}
                {!isMobile && (
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 90, mr: 0.5 }}>
                        <InputLabel sx={{ fontSize: '0.8rem' }}>Type</InputLabel>
                        <Select
                            value={selectedChartType}
                            onChange={(e) => setSelectedChartType(e.target.value)}
                            label="Type"
                            sx={{ fontSize: '0.8rem', height: '30px' }}
                        >
                            {chartTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem' }}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Timeframe buttons */}
                {timeframes.map((timeframe) => (
                    <Button
                        key={timeframe}
                        variant={selectedTimeframe === timeframe ? 'contained' : 'outlined'}
                        onClick={() => setSelectedTimeframe(timeframe)}
                        size="small"
                        sx={{
                            minWidth: { xs: '38px', sm: '42px' },
                            px: { xs: 0.5, sm: 1 },
                            height: '30px',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                    >
                        {timeframe}
                    </Button>
                ))}
            </Box>
        </Paper>
    );
};

export default InteractiveStockChart;